/**
 * Utilidades de autorización y redirección
 *
 * Funciones para manejar la lógica de autorización basada en roles
 * y determinar las rutas de redirección apropiadas.
 */

/**
 * Determina la ruta de redirección basada en los roles del usuario
 *
 * @param roles - Array de roles del usuario
 * @returns Ruta de redirección apropiada
 */
export const getRedirectPathByRole = (roles: string[] | undefined): string => {
  if (!roles || !Array.isArray(roles)) {
    return "/";
  }
  // Jerarquía de redirección por prioridad de roles
  if (roles.includes("Admin")) {
    return "/admin";
  }

  if (roles.includes("Cajero")) {
    return "/pos";
  }

  if (roles.includes("Apoderado")) {
    return "/dashboard";
  }

  if (roles.includes("Estudiante")) {
    return "/student-dashboard";
  }

  if (roles.includes("Personal de Entrega")) {
    return "/scan";
  }

  // Ruta por defecto para otros roles
  return "/";
};

/**
 * Verifica si un usuario tiene un rol específico
 *
 * @param userRoles - Roles del usuario
 * @param requiredRole - Rol requerido
 * @returns true si el usuario tiene el rol requerido
 */
export const hasRole = (
  userRoles: string[] | undefined,
  requiredRole: string
): boolean => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }
  return userRoles.includes(requiredRole);
};

/**
 * Verifica si un usuario tiene al menos uno de los roles especificados
 *
 * @param userRoles - Roles del usuario
 * @param requiredRoles - Roles requeridos (al menos uno)
 * @returns true si el usuario tiene al menos uno de los roles requeridos
 */
export const hasAnyRole = (
  userRoles: string[] | undefined,
  requiredRoles: string[]
): boolean => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }
  return userRoles.some((role) => requiredRoles.includes(role));
};

/**
 * Verifica si un usuario tiene todos los roles especificados
 *
 * @param userRoles - Roles del usuario
 * @param requiredRoles - Roles requeridos (todos)
 * @returns true si el usuario tiene todos los roles requeridos
 */
export const hasAllRoles = (
  userRoles: string[] | undefined,
  requiredRoles: string[]
): boolean => {
  if (!userRoles || !Array.isArray(userRoles)) {
    return false;
  }
  return requiredRoles.every((role) => userRoles.includes(role));
};

/**
 * Obtiene el rol principal del usuario (el de mayor prioridad)
 *
 * @param roles - Roles del usuario
 * @returns Rol principal o null si no hay roles
 */
export const getPrimaryRole = (roles: string[] | undefined): string | null => {
  if (!roles || !Array.isArray(roles)) {
    return null;
  }
  const roleHierarchy = [
    "Admin",
    "Cajero",
    "Apoderado",
    "Funcionario",
    "Estudiante",
    "Personal de Entrega",
  ];

  for (const role of roleHierarchy) {
    if (roles.includes(role)) {
      return role;
    }
  }

  return null;
};

/**
 * Obtiene el nombre amigable de un rol
 *
 * @param role - Rol del sistema
 * @returns Nombre amigable del rol
 */
export const getRoleDisplayName = (role: string): string => {
  const roleNames: Record<string, string> = {
    Admin: "Administrador",
    Apoderado: "Apoderado",
    Cajero: "Cajero",
    Funcionario: "Funcionario",
    Estudiante: "Estudiante",
    "Personal de Entrega": "Personal de Entrega",
  };

  return roleNames[role] || role;
};

/**
 * Obtiene la descripción de un rol
 *
 * @param role - Rol del sistema
 * @returns Descripción del rol
 */
export const getRoleDescription = (role: string): string => {
  const roleDescriptions: Record<string, string> = {
    Admin: "Acceso completo al sistema de administración",
    Apoderado: "Gestión de hijos y consulta de consumos",
    Cajero: "Operaciones de punto de venta",
    Funcionario: "Acceso a información administrativa",
    Estudiante: "Acceso limitado a servicios estudiantiles",
    "Personal de Entrega": "Gestión de entregas y logística",
  };

  return roleDescriptions[role] || "Sin descripción disponible";
};
